'use client';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';

import type { PaymentReason, PaymentReasonType } from '~features/annals';
import { BotPaywallModal } from '~features/botPaywall';
import { useCurrentBot } from '~hooks/useCurrentBot';

import { BotPaywallContext } from './index';
import { useBotPaywall } from './useBotPaywall';

type Props = PropsWithChildren<{
	recipientId: string;
	messagesLength: number;
	reason: PaymentReason;
	reasonType: PaymentReasonType;
}>;

export const BotPaywallContextProvider = ({
	children,
	recipientId,
	messagesLength,
	reason,
	reasonType,
}: Props) => {
	const { bot, botLoading } = useCurrentBot({ recipientId });

	const {
		isOpen,
		canSendMessages,
		reason: contextReason,
		reasonType: contextReasonType,
		openBotPaywall,
		closeBotPaywall,
	} = useBotPaywall(bot, messagesLength);

	const context = useMemo(() => {
		return {
			isOpen,
			canSendMessages,
			reason: contextReason ?? reason,
			reasonType: contextReasonType ?? reasonType,
			botId: bot.clientId,
			openBotPaywall,
			closeBotPaywall,
		};
	}, [
		isOpen,
		canSendMessages,
		contextReason,
		contextReasonType,
		reason,
		reasonType,
		bot.clientId,
		openBotPaywall,
		closeBotPaywall,
	]);

	return (
		<BotPaywallContext.Provider value={context}>
			{children}
			{botLoading ? null : (
				<BotPaywallModal
					botClientId={isOpen ? recipientId : void 0}
					reason={contextReason ?? reason}
					reasonType={contextReasonType ?? reasonType}
					promo={true}
					onClose={closeBotPaywall}
				/>
			)}
		</BotPaywallContext.Provider>
	);
};
